import React, { useState } from "react";
import styled from "styled-components";
import { Typography } from "@mui/material";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Text } from "@react-three/drei";

const MainContainer = styled.div`
  height: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #000;
  color: #fff;
  padding: 20px;
`;

const ContentContainer = styled.div`
  margin: 20px;
  width: 100%;
  max-width: 960px;
  padding: 2rem;
`;

const Legend = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  color: #fff;
`;

const LegendDot = styled.div`
  width: 12px;
  height: 12px;
  background-color: yellow;
  border-radius: 50%;
  margin-right: 8px;
`;

const ListContainer = styled.div`
  margin-top: 20px;
  padding: 20px;
  background-color: #111;
  border-radius: 8px;
  max-width: 960px;
  color: #fff;
`;

const ListItem = styled.div`
  margin-bottom: 10px;
  font-size: 16px;
  line-height: 1.5;
`;

export default function VAL3DDiagram() {
  const [loading, setLoading] = useState(false);

  return (
    <MainContainer>
      <ContentContainer>
        <Legend>
          <LegendDot /> <Typography variant="body1">Emotion</Typography>
        </Legend>

        <Typography variant="h4" align="center" gutterBottom>
          3D Emotion Space (VAL Model)
        </Typography>

        <Canvas
          camera={{ position: [4, 4, 4], fov: 45 }}
          style={{ height: "500px" }}
        >
          <ambientLight intensity={0.5} />
          <pointLight position={[10, 10, 10]} />
          <VAL3D />
          <OrbitControls enableZoom={true} maxDistance={10} minDistance={3} />
        </Canvas>
      </ContentContainer>

      {/* Emotion Labels List */}
      <ListContainer>
        <Typography variant="h5" align="center" gutterBottom>
          Example Emotions in VAL Space
        </Typography>
        {emotionLabels.map((emotion, index) => (
          <ListItem key={index}>
            {index + 1}. {emotion.label} <b>({emotion.category})</b>
          </ListItem>
        ))}
      </ListContainer>
    </MainContainer>
  );
}

function VAL3D() {
  const [hovered, setHovered] = useState(null);

  return (
    <>
      {/* Axis lines */}
      <mesh>
        {/* Valence Axis (X) */}
        <line>
          <bufferGeometry attach="geometry">
            <bufferAttribute
              attach="attributes-position"
              count={2}
              array={new Float32Array([-2, 0, 0, 2, 0, 0])}
              itemSize={3}
            />
          </bufferGeometry>
          <lineBasicMaterial color="white" />
        </line>
        {/* Arousal Axis (Y) */}
        <line>
          <bufferGeometry attach="geometry">
            <bufferAttribute
              attach="attributes-position"
              count={2}
              array={new Float32Array([0, -2, 0, 0, 2, 0])}
              itemSize={3}
            />
          </bufferGeometry>
          <lineBasicMaterial color="white" />
        </line>
        {/* Learning Axis (Z) */}
        <line>
          <bufferGeometry attach="geometry">
            <bufferAttribute
              attach="attributes-position"
              count={2}
              array={new Float32Array([0, 0, -2, 0, 0, 2])}
              itemSize={3}
            />
          </bufferGeometry>
          <lineBasicMaterial color="white" />
        </line>
      </mesh>

      {/* Axis Labels */}
      <Text position={[2.3, 0, 0]} fontSize={0.25} color="lightgreen">
        Valence (+)
      </Text>
      <Text position={[-2.5, 0, 0]} fontSize={0.25} color="lightgreen">
        Valence (-)
      </Text>
      <Text position={[0, 2.3, 0]} fontSize={0.25} color="lightgreen">
        Arousal (High)
      </Text>
      <Text position={[0, -2.5, 0]} fontSize={0.25} color="lightgreen">
        Arousal (Low)
      </Text>
      <Text position={[0, 0, 2.3]} fontSize={0.25} color="lightgreen">
        Constructive Learning
      </Text>
      <Text position={[0, 0, -2.5]} fontSize={0.25} color="lightgreen">
        Un-learning
      </Text>

      {/* Plotting Emotions as dots */}
      {emotionLabels.map((emotion, index) => (
        <mesh
          key={index}
          position={emotion.position}
          onPointerOver={() => setHovered(emotion)}
          onPointerOut={() => setHovered(null)}
        >
          {/* Dot */}
          <sphereGeometry args={[0.12, 16, 16]} />
          <meshStandardMaterial color="yellow" />

          {/* Display label on hover */}
          {hovered === emotion && (
            <Text position={[0, 0.3, 0]} fontSize={0.18} color="yellow">
              {`${emotion.label} (${emotion.category})`}
            </Text>
          )}
        </mesh>
      ))}
    </>
  );
}

// Example emotion labels and their positions in the 3D space
// X: Valence (-2 to 2), Y: Arousal (-2 to 2), Z: Learning (-2 to 2)
const emotionLabels = [
  {
    label: "Awe",
    position: [2, 2, 2],
    category: "Positive, High Arousal, Constructive Learning",
  },
  {
    label: "Satisfaction",
    position: [2, 1, 1],
    category: "Positive, Medium Arousal, Constructive Learning",
  },
  {
    label: "Curiosity",
    position: [1, 2, 2],
    category: "Positive, High Arousal, Constructive Learning",
  },
  {
    label: "Calm",
    position: [2, -2, 1],
    category: "Positive, Low Arousal, Constructive Learning",
  },
  {
    label: "Relief",
    position: [1, -1, 1],
    category: "Positive, Low Arousal, Constructive Learning",
  },
  {
    label: "Boredom",
    position: [-2, -2, -1],
    category: "Negative, Low Arousal, Un-learning",
  },
  {
    label: "Anxiety",
    position: [-2, 2, -2],
    category: "Negative, High Arousal, Un-learning",
  },
  {
    label: "Frustration",
    position: [-2, 1, -2],
    category: "Negative, Medium Arousal, Un-learning",
  },
  {
    label: "Disappointment",
    position: [-2, -1, -2],
    category: "Negative, Low Arousal, Un-learning",
  },
  {
    label: "Surprise",
    position: [0, 2, 2],
    category: "Neutral, High Arousal, Constructive Learning",
  },
  {
    label: "Confusion",
    position: [0, 1, -2],
    category: "Neutral, Medium Arousal, Un-learning",
  },
  {
    label: "Hope",
    position: [2, 2, 1],
    category: "Positive, High Arousal, Constructive Learning",
  },
  {
    label: "Regret",
    position: [-2, -1, -2],
    category: "Negative, Low Arousal, Un-learning",
  },
];
